import { ArrowRightIcon } from "lucide-react";
import { cn } from "../../lib/utils";

const BentoGrid = ({ children, className, ...props }) => {
  return (
    <div
      className={cn(
        "grid w-full gap-3",
        // 强制桌面端使用3列2行布局，调整合适的高度
        "!grid-cols-3 !grid-rows-2 !h-[640px]",
        // 大屏幕使用更大的高度
        "lg:!grid-cols-3 lg:!grid-rows-2 lg:!h-[680px]",
        "xl:!grid-cols-3 xl:!grid-rows-2 xl:!h-[720px]",
        // 中等屏幕也强制使用3列2行
        "md:!grid-cols-3 md:!grid-rows-2 md:!h-[600px]",
        // 只在小屏幕使用单列布局
        "sm:!grid-cols-1 sm:!grid-rows-auto sm:!gap-4 sm:!h-auto",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

const BentoCard = ({
  name,
  className,
  background,
  Icon,
  description,
  href,
  cta,
  onClick,
  children,
  ...props
}) => {
  // 处理点击事件
  const handleClick = () => {
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <div
      key={name}
      className={cn(
        "group relative flex flex-col justify-between overflow-hidden",
        // 圆角 - 保持桌面端原有设计
        "rounded-2xl lg:rounded-3xl",
        // 基础样式
        "bg-white dark:bg-gray-900",
        // 阴影效果
        "shadow-sm hover:shadow-lg",
        "border-0",
        // 交互效果 - 保持原有动画
        "transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
        "hover:scale-[1.01] hover:-translate-y-0.5",
        // 光标样式
        (onClick || href) ? "cursor-pointer" : "cursor-default",
        // 移动端最小高度
        "sm:min-h-[140px]",
        className,
      )}
      onClick={handleClick}
      {...props}
    >
    {/* 背景层 */}
    {background && (
      <div className="absolute inset-0 overflow-hidden rounded-2xl lg:rounded-3xl">
        {background}
      </div>
    )}
    
    {/* 内容层 - 确保在背景之上且可交互 */}
    <div className="relative z-20 flex h-full flex-col justify-between p-0">
      {/* 如果有自定义children，直接渲染 */}
      {children}
      
      {/* 如果没有自定义children，使用默认布局 */}
      {!children && (
        <>
          {/* Icon区域 - 保持桌面端原有内边距，移动端稍微调整 */}
          {Icon && (
            <div className="p-6 pb-0 sm:p-4 sm:pb-0 md:p-6 md:pb-0">
              <Icon className="h-8 w-8 sm:h-6 sm:w-6 md:h-8 md:w-8 text-gray-700 dark:text-gray-300 transition-all duration-300" />
            </div>
          )}
          
          {/* 内容区域 - 移动端字体稍小 */}
          {(name || description) && (
            <div className="flex-1 flex flex-col justify-center p-6 sm:p-4 md:p-6">
              {name && (
                <h3 className="text-xl sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {name}
                </h3>
              )}
              {description && (
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  {description}
                </p>
              )}
            </div>
          )}

          {/* CTA区域 - 移动端内边距调整 */}
          {cta && (
            <div className="p-6 pt-0 sm:p-4 sm:pt-0 md:p-6 md:pt-0">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 transition-colors group-hover:text-gray-900 dark:group-hover:text-gray-100">
                {cta}
                <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
            </div>
          )}
        </>
      )}
    </div>
    
    {/* 悬停覆盖层 - 保持原有效果 */}
    <div className="absolute inset-0 rounded-2xl lg:rounded-3xl bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none" />
  </div>
  );
};

export { BentoCard, BentoGrid }; 